import {
  UnifiedTrackingMachine,
  UnifiedTrackingMachineAdBeginEvent,
  UnifiedTrackingMachineAdBreakBeginEvent,
  type UnifiedTrackingMachineEvent,
  UnifiedTrackingMachineLoadEvent,
  UnifiedTrackingMachineNewTrackingEntriesEvent,
  UnifiedTrackingMachineTimeUpdateEvent,
} from '../tracking/UnifiedTrackingMachine';
import type { AdobeGateway } from './AdobeGateway';
import type { UseTrackingMappedMedia } from '../../plugins/Tracking';
import { createMediaObject } from '../../services/Adobe/functions/createMediaObject';
import { AdobeTimeTableMapper } from './TimeTable';
import { type AdobeTrackingStrategy } from './AdobeTrackingStrategy';
import { AdobeVodTrackingStrategy } from './AdobeVodTrackingStrategy';
import { AdobeLiveTrackingStrategy } from './AdobeLiveTrackingStrategy';
import { AdobeNullTrackingStrategy } from './AdobeNullTrackingStrategy';

export enum MediaHeartbeatEvents {
  seekStart = 'seekStart',
  seekComplete = 'seekComplete',
  pause = 'pause',
  play = 'play',
  bufferStart = 'bufferStart',
  bufferComplete = 'bufferComplete',
  adStart = 'adStart',
  adComplete = 'adComplete',
  adBreakStart = 'adBreakStart',
  adBreakComplete = 'adBreakComplete',
}

export type AdobeQualityOfExperience = {
  droppedFrames: number;
  currentBitrate: number;
};

export class AdobeTrackingMachine extends UnifiedTrackingMachine {
  private gateway: AdobeGateway;
  private strategy: AdobeTrackingStrategy = new AdobeNullTrackingStrategy(); // Before we have a strategy, we need to have a null strategy to avoid errors
  private playerName: string;
  private readonly useActive: boolean;

  constructor(gateway: AdobeGateway, playerName: string, useActive = false) {
    super();
    this.gateway = gateway;
    this.useActive = useActive;
    this.playerName = playerName;
  }

  send(event: UnifiedTrackingMachineEvent) {
    this.gateway.setSource(event);
    super.send(event);
  }

  protected onDestroy(): void {
    this.strategy.onDestroy();
  }

  protected onBuffering(): void {
    this.strategy.onBuffering();
  }

  protected onPause(): void {
    this.strategy.onPause();
  }

  protected onStop(): void {
    this.strategy.onStop();
  }

  protected onAdEnd(): void {
    this.strategy.onAdEnd();
  }

  protected onAdBegin(event: UnifiedTrackingMachineAdBeginEvent): void {
    this.strategy.onAdBegin(event);
  }

  protected onAdBreakEnd(): void {
    this.strategy.onAdBreakEnd();
  }

  protected onAdBreakBegin(event: UnifiedTrackingMachineAdBreakBeginEvent): void {
    this.strategy.onAdBreakBegin(event);
  }

  protected onSeeking(): void {
    this.strategy.onSeeking();
  }

  protected onNewTrackingEntries(event: UnifiedTrackingMachineNewTrackingEntriesEvent): void {
    const tracking = event.value.tracking!;

    if (tracking?.__typename !== 'LiveTracking') {
      return;
    }

    const entries = AdobeTimeTableMapper.map(tracking);

    if (this.useActive && event?.value?.entity && 'epgUpdateType' in event.value.entity) {
      const epgUpdateType = event.value.entity.epgUpdateType!;
      this.strategy.onTrackingEntries(entries, epgUpdateType, tracking.adobe?.active?.program);
    } else {
      this.strategy.onTrackingEntries(entries);
    }
  }

  protected onTimeUpdate(event: UnifiedTrackingMachineTimeUpdateEvent): void {
    this.strategy.onTimeUpdate(event);
  }

  protected onLoad(event: UnifiedTrackingMachineLoadEvent): void {
    this.gateway.onMedia(event.value.media);

    // If we have a strategy, we need to destroy it first
    if (this.strategy) {
      this.strategy.onDestroy();
    }

    if (event.value.media.metadata.isLive) {
      this.strategy = new AdobeLiveTrackingStrategy(this.gateway, this.playerName);
    } else {
      this.strategy = new AdobeVodTrackingStrategy(this.gateway, this.playerName);
    }

    this.strategy.onLoad(event);
  }
}

export function getMediaObject(
  adobe: { title: string; id: string; duration: number },
  media: UseTrackingMappedMedia
) {
  return createMediaObject(
    adobe.title,
    adobe.id,
    media.entity.duration ?? Math.floor(adobe.duration / 1000),
    media.metadata.isLive ? 'live' : 'vod',
    'video'
  );
}
